import { action, computed, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { Config } from '~domain/entities/Config';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { StoresMap } from '~domain/entities/StoresMap';
import { StoreDetailsLoadedUseCase } from '~use-cases/StoreDetailsLoadedUseCase';
import { GetDirectionsClickedUseCase } from '~use-cases/GetDirectionsClickedUseCase';
import { StoreInfoClickedUseCase } from '~use-cases/StoreInfoClickedUseCase';
import { StorePhoneClickedUseCase } from '~use-cases/StorePhoneClickedUseCase';
import { StoresRepository } from '~repositories/StoresRepository';
import { getGMapsDirectionsUrl } from '../../../utils/formatters';
import { MarkerColors } from '@estee/elc-static-maps';
import { debounce } from '../../utils/debounce';
import { UrlFormatter } from '@estee/elc-formatters';
import { ToggleAddToCalendarModalUseCase } from '~use-cases/ToggleAddToCalendarModalUseCase';
import { RedirectionLinkClickedUseCase } from '~use-cases/RedirectionLinkClickedUseCase';
import { LoadInStorePickupBannerUseCase } from '~use-cases/LoadInStorePickupBannerUseCase';
import { StoreDetailsRepository } from '~repositories/StoreDetailsRepository';
import { NearbyStoresLoadedUseCase } from '~use-cases/NearbyStoresLoadedUseCase';
import { Store } from '~domain/entities/Store';
import { events, emit } from '@estee/elc-service-bus';
import { ExpandedStoreHoursIdUseCase } from '~use-cases/ExpandedStoreHoursIdUseCase';
import { LoadInStoreServicesUseCase } from '~use-cases/LoadInStoreServicesUseCase';
import { IInStoreService } from '~domain/entities/InStoreService';

export interface IStaticMapMarker {
    latitude: number;
    longitude: number;
    label: string;
    color: MarkerColors | number;
}

export class StoreDetailsViewController extends ViewController {
    @observable private storesMap: StoresMap;
    @observable public configService: Config;
    @observable private storesRepository: StoresRepository;
    @observable private storeDetailsRepository: StoreDetailsRepository;
    @observable private screenWidth: number;
    private urlFormatter: UrlFormatter;

    constructor(
        storesRepository: StoresRepository,
        storeDetailsRepository: StoreDetailsRepository
    ) {
        super();

        this.configService = diContainer.get(serviceNames.config);
        this.storesMap = diContainer.get(serviceNames.storesMap);
        this.urlFormatter = diContainer.get(serviceNames.urlFormatter);
        this.storesRepository = storesRepository;
        this.storeDetailsRepository = storeDetailsRepository;
    }

    @action
    public loaded = async (storeId: string, screenWidth: number) => {
        this.isLoading = true;
        await new StoreDetailsLoadedUseCase(storeId).execute();
        if (this.configService.inStorePickupBannerNodeId) {
            await new LoadInStorePickupBannerUseCase(
                this.storeDetailsRepository,
                this.configService
            ).execute();
        }
        if (
            this.configService.inStoreServicesNodeId &&
            !this.storeDetailsRepository.servicesTemplatesContent.size
        ) {
            await new LoadInStoreServicesUseCase(
                this.storeDetailsRepository,
                this.configService
            ).execute();
        }
        await new NearbyStoresLoadedUseCase({
            lat: this.markers[0].latitude,
            lng: this.markers[0].longitude
        }).execute();
        this.triggerScreenWidthChange(screenWidth);
        this.isLoading = false;
    };

    @action
    private refreshScreenWidth = (screenWidth: number) => {
        this.screenWidth = screenWidth;
    };

    private debouncedRefreshScreenWidth = debounce(this.refreshScreenWidth, 1000, true);

    public triggerScreenWidthChange = (screenWidth: number) => {
        if (screenWidth !== this.screenWidth) {
            this.debouncedRefreshScreenWidth(screenWidth);
        }
    };

    @computed
    private get markers(): IStaticMapMarker[] {
        if (this.currentStore) {
            const {
                geographical: { coordinates },
                name
            } = this.currentStore;

            const currentStoreMarker: IStaticMapMarker = {
                latitude: coordinates.lat,
                longitude: coordinates.lon,
                label: name,
                color: MarkerColors.BLACK
            };

            return [currentStoreMarker];
        }

        return [];
    }

    @computed
    private get breadcrumbsUrls() {
        return {
            homeUrl: this.urlFormatter.langPrefixUrl('/'),
            storesLandingUrl: this.urlFormatter.langPrefixUrl(
                `/${this.configService.config.home_path}`
            )
        };
    }

    @computed
    private get isInStoreServicesVisible() {
        return (
            (this.configService.config.featureFlags &&
                this.configService.config.featureFlags.showOabServices) ||
            false
        );
    }

    @computed
    public get inStoreServices() {
        return this.storesRepository.stores[0]?.inStoreServices;
    }

    @computed
    public get formattedInStoreServices() {
        const storeServices = this.inStoreServices;
        const cmsServices = this.servicesContent;

        if (!storeServices || !storeServices.length || !cmsServices.size) {
            return [];
        } else {
            const filteredServices = storeServices.filter((service) => {
                return cmsServices.has(service.onlineContentId);
            });

            filteredServices.forEach((service) => {
                const cmsService = cmsServices.get(service.onlineContentId);
                if (cmsService) {
                    service.description = cmsService.description as string;
                    service.imageUrl = cmsService.image.src as string;
                }
            });

            return filteredServices as IInStoreService[];
        }
    }

    @computed
    get currentStore() {
        return this.storesRepository.stores[0];
    }

    @computed
    public get nearbyStores() {
        return this.storesRepository.stores.slice(1);
    }
    @computed
    get directionsURL() {
        const {
            geographical: { coordinates },
            address
        } = this.currentStore;

        return getGMapsDirectionsUrl(coordinates.lat, coordinates.lon, address.formattedAddress);
    }

    @action
    public onGetDirections = (storeName?: string) => {
        const { id, name } = this.currentStore;
        emit(events.STORE_GET_DIRECTIONS_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_details_get_directions_click',
            eventAction: `${storeName}`,
            eventLabel: `${storeName}`
        });
        new GetDirectionsClickedUseCase(id, name, this.directionsURL).execute();
    };

    @action
    public onGetDirectionsToNearbyStore = (store: Store) => {
        const {
            id,
            name,
            geographical: { coordinates },
            address
        } = store;
        const formattedAddress = `${address.neighbourhood} ${address.city} ${address.postal_code}`;
        emit(events.STORE_NEAR_BY_GET_DIRECTIONS_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_near_by_get_directions_click',
            eventAction: `${name}`,
            eventLabel: `${name}`
        });
        new GetDirectionsClickedUseCase(
            id,
            name,
            getGMapsDirectionsUrl(coordinates.lat, coordinates.lon, formattedAddress)
        ).execute();
    };

    @action
    public getNearbyStoreMarkers(store: Store): IStaticMapMarker[] {
        const {
            name,
            geographical: { coordinates }
        } = store;

        const storeMarker: IStaticMapMarker = {
            latitude: coordinates.lat,
            longitude: coordinates.lon,
            label: name,
            color: MarkerColors.BLUE
        };

        return [storeMarker];
    }

    @action
    public onPhoneClick = () => {
        const { id, name } = this.currentStore;
        new StorePhoneClickedUseCase(id, name).execute();
    };

    @action
    public onStoreInfoLinkClick = () => {
        const { id, name } = this.currentStore;
        emit(events.STORE_DETAILS_ADDRESS_LINK_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_details_address_link_click',
            eventAction: `${name}`,
            eventLabel: `${name}`
        });
        new StoreInfoClickedUseCase(id, name).execute();
    };

    @action
    public onServiceCardBookAppointmentClick = (storeName: string, serviceName: string) => {
        emit(events.STORE_BOOK_APPOINTMENT_LINK_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_service_book_appointment_click',
            eventAction: `${storeName} - ${serviceName}`,
            eventLabel: 'book_a_service'
        });
    };

    @action
    public onAddToCalendarClick = () => {
        new ToggleAddToCalendarModalUseCase(true).execute();
    };

    @action
    public onAddToCalendarModalCloseClick = () => {
        new ToggleAddToCalendarModalUseCase(false).execute();
    };

    @action
    public onInStorePickupBannerShopNowClick = (url: string, labelText?: string) => {
        emit(events.STORE_DETAILS_IN_STORE_PICKUP_LINK_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_details_clicks',
            eventAction: 'click',
            eventLabel: `${labelText}`
        });
        new RedirectionLinkClickedUseCase(url).execute();
    };

    @action
    public onInStorePickupBannerLearnMoreClick = (url: string, labelText?: string) => {
        emit(events.STORE_DETAILS_IN_STORE_PICKUP_LINK_CLICKED, {
            eventName: 'store_details',
            eventCategory: 'store_details_clicks',
            eventAction: 'click',
            eventLabel: `${labelText}`
        });
        new RedirectionLinkClickedUseCase(url).execute();
    };

    @action
    public onExpandedStoreHoursIdChange = (listType?: string, storeId?: string) => {
        new ExpandedStoreHoursIdUseCase(listType, storeId).execute();
    };

    @action
    public storeHoursToggled = (isExpanded: boolean, name: string) => {
        emit(events.STORE_OPENING_HOURS_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_map_selection',
            eventAction: isExpanded ? 'expand' : 'collapse',
            eventLabel: name
        });
    };

    @computed
    get inStorePickupBanner() {
        const { inStorePickupBanner } = this.storeDetailsRepository;

        const isBannerPublished = inStorePickupBanner
            ? !Object.values(inStorePickupBanner).every((value) => value === undefined)
            : false;

        return isBannerPublished ? inStorePickupBanner : null;
    }

    public getStoreImage = async (url: string) => {
        const response = await this.storeDetailsRepository.checkStoreImage(url);

        return response;
    };

    @computed
    get servicesContent() {
        const { servicesTemplatesContent } = this.storeDetailsRepository;

        return servicesTemplatesContent;
    }

    @computed
    get data() {
        return {
            config: this.configService.config,
            storesMap: this.storesMap,
            store: this.currentStore,
            nearbyStores: this.nearbyStores,
            isLoading: this.isLoading,
            onGetDirections: this.onGetDirections,
            onGetDirectionsToNearbyStore: this.onGetDirectionsToNearbyStore,
            getNearbyStoreMarkers: this.getNearbyStoreMarkers,
            markers: this.markers,
            screenWidth: this.screenWidth,
            breacrumbsUrls: this.breadcrumbsUrls,
            isInStoreServicesVisible: this.isInStoreServicesVisible,
            isAddToCalendarModelOpen: this.storesRepository.isAddToCalendarModalOpen,
            inStorePickupBanner: this.inStorePickupBanner,
            expandedStoreHoursId: this.storesRepository.expandedStoreHoursId,
            isStoreImageExists: this.storeDetailsRepository.storeImageExists,
            storeDetailsDistance: this.storesRepository.storeDetailsDistance,
            formattedInStoreServices: this.formattedInStoreServices
        };
    }
}
